import React from 'react';
import { Box } from '@mui/material';
import { BrandKit, SceneType } from './types';

// Static, brand-coloured thumbnail of a scene's layout (prototype `sc.svg`).
// Purely schematic — the real motion lives in the renderer's templates.
const ScenePreview: React.FC<{ type: SceneType; content: Record<string, any>; kit: BrandKit; width?: number }> = ({
  type,
  content,
  kit,
  width = 160
}) => {
  const h = Math.round((width * 9) / 16);
  const { outline: stroke, fill, text } = kit;
  const bar = (x: number, y: number, w: number, hh = 5, c = text, o = 0.85) => (
    <rect x={x} y={y} width={w} height={hh} rx={hh / 2} fill={c} opacity={o} />
  );
  const items = (content.points || content.steps || content.takeaways || []).length || 3;
  let body: React.ReactNode;
  switch (type) {
    case 'statement':
      body = (
        <>
          {bar(24, 30, 26, 4, fill, 1)}
          {bar(24, 42, 100, 8)}
          {bar(24, 56, 70, 8)}
        </>
      );
      break;
    case 'key_points':
    case 'summary':
      body = (
        <>
          {bar(16, 14, 22, 3, fill, 1)}
          {bar(16, 22, 80, 7)}
          {Array.from({ length: Math.min(items, 5) }).map((_, i) => (
            <g key={i}>
              <circle cx={20} cy={42 + i * 10} r={2.6} fill={fill} />
              {bar(28, 40 + i * 10, 60 + (i % 2) * 14, 4)}
            </g>
          ))}
        </>
      );
      break;
    case 'two_column':
      body = (
        <>
          {bar(16, 18, 22, 3, fill, 1)}
          {bar(16, 26, 62, 7)}
          {bar(16, 40, 70, 4)}
          {bar(16, 48, 56, 4)}
          <circle cx={122} cy={45} r={22} fill="none" stroke={stroke} strokeWidth={2} />
          <path d="M112 45 l6 6 l14 -14" fill="none" stroke={fill} strokeWidth={3} strokeLinecap="round" />
        </>
      );
      break;
    case 'numbered_steps':
      body = (
        <>
          {bar(16, 16, 80, 7)}
          {Array.from({ length: Math.min(items, 4) }).map((_, i) => (
            <g key={i}>
              <circle cx={22} cy={36 + i * 12} r={5} fill={fill} />
              {bar(32, 34 + i * 12, 70 - (i % 2) * 10, 4)}
            </g>
          ))}
        </>
      );
      break;
    case 'scenario_card':
      body = (
        <>
          <rect x={20} y={14} width={120} height={62} rx={8} fill="none" stroke={stroke} strokeWidth={2} />
          {bar(30, 24, 22, 3, fill, 1)}
          {bar(30, 32, 70, 7)}
          {bar(30, 46, 96, 4)}
          {bar(30, 54, 84, 4)}
          {bar(30, 66, 40, 3, fill, 1)}
        </>
      );
      break;
    case 'stat_highlight':
      body = (
        <>
          <text
            x={80}
            y={48}
            textAnchor="middle"
            fontSize={30}
            fontWeight={800}
            fill={fill}
            fontFamily="Inter, sans-serif"
          >
            {(content.value || '42%').slice(0, 5)}
          </text>
          {bar(44, 56, 72, 5)}
          {bar(60, 66, 40, 3, text, 0.5)}
        </>
      );
      break;
    case 'diagram':
      body = (
        <>
          {bar(16, 12, 70, 6)}
          <line x1={80} y1={50} x2={40} y2={72} stroke={stroke} strokeWidth={2} />
          <line x1={80} y1={50} x2={120} y2={72} stroke={stroke} strokeWidth={2} />
          <line x1={80} y1={50} x2={80} y2={28} stroke={stroke} strokeWidth={2} />
          <rect x={64} y={42} width={32} height={16} rx={8} fill={fill} />
          <rect x={26} y={66} width={28} height={13} rx={6.5} fill={kit.background} stroke={stroke} strokeWidth={2} />
          <rect x={106} y={66} width={28} height={13} rx={6.5} fill={kit.background} stroke={stroke} strokeWidth={2} />
          <rect x={66} y={20} width={28} height={13} rx={6.5} fill={kit.background} stroke={stroke} strokeWidth={2} />
        </>
      );
      break;
    default:
      body = null;
  }
  return (
    <Box
      component="svg"
      viewBox="0 0 160 90"
      sx={{
        width,
        height: h,
        borderRadius: '6px',
        display: 'block',
        flexShrink: 0,
        background: kit.background,
        border: `1px solid ${'#2A2A2A'}`
      }}
    >
      {body}
    </Box>
  );
};

export default ScenePreview;

// Opening / closing thumbnails (locked cards).
export const OpeningPreview: React.FC<{ kit: BrandKit; moduleName: string; lessonName: string; width?: number }> = ({
  kit,
  width = 160
}) => (
  <Box
    component="svg"
    viewBox="0 0 160 90"
    sx={{
      width,
      height: Math.round((width * 9) / 16),
      borderRadius: '6px',
      display: 'block',
      flexShrink: 0,
      background: kit.background,
      border: '1px solid #2A2A2A'
    }}
  >
    <rect x={16} y={16} width={16} height={16} rx={7} fill="#F79420" />
    <rect x={16} y={40} width={30} height={3} rx={1.5} fill={kit.fill} />
    <rect x={16} y={48} width={90} height={9} rx={4.5} fill={kit.text} opacity={0.9} />
    <rect x={16} y={62} width={50} height={4} rx={2} fill={kit.text} opacity={0.55} />
    <rect x={16} y={76} width={40} height={2.5} rx={1.25} fill={kit.fill} />
  </Box>
);

export const ClosingPreview: React.FC<{ kit: BrandKit; width?: number }> = ({ kit, width = 160 }) => (
  <Box
    component="svg"
    viewBox="0 0 160 90"
    sx={{
      width,
      height: Math.round((width * 9) / 16),
      borderRadius: '6px',
      display: 'block',
      flexShrink: 0,
      background: kit.background,
      border: '1px solid #2A2A2A'
    }}
  >
    <rect x={66} y={26} width={28} height={28} rx={12} fill="#F79420" />
    <circle cx={75} cy={37} r={1.8} fill="#FDFDFD" />
    <circle cx={85} cy={37} r={1.8} fill="#FDFDFD" />
    <path d="M74 43 Q80 46 86 43 Q80 50 74 43 Z" fill="#FDFDFD" />
    <rect x={62} y={62} width={36} height={4} rx={2} fill={kit.text} opacity={0.7} />
  </Box>
);
